import React from "react";
import Hero from "./Hero";
import Features from "./Features";
import Pricing from "./Pricing";
import FAQ from "./FAQ";
import "./landing.css";

function handleBlogNavClick(onNavigateBlog) {
  return (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onNavigateBlog();
  };
}

export default function LandingPage({
  onStartTrial,
  onViewDashboard,
  onLogin,
  onNavigateBlog,
  isAuthenticated,
  analysis,
  context,
}) {
  return (
    <div className="landing-shell">
      <div className="landing-bg" aria-hidden="true">
        <div className="landing-orb landing-orb-1" />
        <div className="landing-orb landing-orb-2" />
        <div className="landing-orb landing-orb-3" />
        <div className="landing-orb landing-orb-4" />
        <div className="landing-grid-overlay" />
      </div>

      <main className="landing-main">
        <header className="landing-nav">
          <button
            type="button"
            className="landing-brand"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <span className="landing-brand-mark" aria-hidden="true">
              <span className="landing-brand-spark" />
            </span>
            <span className="landing-brand-copy">
              <strong>Spike Hunter Online</strong>
              <span>Trading Intelligence</span>
            </span>
          </button>

          <nav className="landing-menu" aria-label="Landing">
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
            <a href="/blog" onClick={handleBlogNavClick(onNavigateBlog)}>
              Blog
            </a>
            <a href="#contact">Contact</a>
          </nav>

          <div className="landing-nav-actions">
            {!isAuthenticated ? (
              <button type="button" className="landing-nav-link" onClick={onLogin}>
                Log In
              </button>
            ) : null}
            <button type="button" className="landing-secondary-button" onClick={onViewDashboard}>
              Dashboard
            </button>
            {!isAuthenticated ? (
              <button type="button" className="landing-primary-button" onClick={onStartTrial}>
                Sign Up Free
              </button>
            ) : null}
          </div>
        </header>

        <Hero
          onSignup={onStartTrial}
          onViewDashboard={onViewDashboard}
          onLogin={onLogin}
          isAuthenticated={isAuthenticated}
          analysis={analysis}
          context={context}
        />

        <div id="features">
          <Features />
        </div>

        <div id="pricing">
          <Pricing onStartTrial={onStartTrial} />
        </div>

        <div id="faq">
          <FAQ />
        </div>

        <section className="landing-final-cta">
          <div className="landing-eyebrow">Ready to Start</div>
          <h2>Start seeing the market differently.</h2>
          <p>
            Join traders who use Spike Hunter Online to read structure,
            spot zones early, and act with confidence.
          </p>
          <div className="landing-final-actions">
            <button type="button" className="landing-primary-button" onClick={onStartTrial}>
              Start Your 7-Day Free Trial
            </button>
            {!isAuthenticated ? (
              <button type="button" className="landing-secondary-button" onClick={onLogin}>
                Log In Instead
              </button>
            ) : null}
          </div>
        </section>

        <footer className="landing-footer" id="contact">
          <div className="landing-footer-top">
            <div className="landing-footer-brand">
              <span className="landing-brand-mark" aria-hidden="true">
                <span className="landing-brand-spark" />
              </span>
              <div className="landing-brand-copy">
                <strong>Spike Hunter Online</strong>
                <span>Trading Intelligence</span>
              </div>
            </div>
            <nav className="landing-footer-nav" aria-label="Footer navigation">
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#faq">FAQ</a>
              <a href="/blog" onClick={handleBlogNavClick(onNavigateBlog)}>
                Blog
              </a>
              <a href="#top">Back to top</a>
            </nav>
          </div>

          <div className="landing-footer-divider" />

          <div className="landing-footer-contact-section">
            <div className="landing-footer-contact-heading">
              <FooterIcon type="headset" />
              Contact Us
            </div>
            <div className="landing-footer-contact-row">
              <a href="mailto:spikehunteronline@gmail.com" className="landing-footer-contact-item">
                <FooterIcon type="email" />
                <div className="landing-footer-contact-detail">
                  <span className="landing-footer-contact-label">Email</span>
                  <span className="landing-footer-contact-value">spikehunteronline@gmail.com</span>
                </div>
              </a>
              <a
                href="http://www.spikehunter.online/"
                className="landing-footer-contact-item"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FooterIcon type="globe" />
                <div className="landing-footer-contact-detail">
                  <span className="landing-footer-contact-label">Website</span>
                  <span className="landing-footer-contact-value">www.spikehunter.online</span>
                </div>
              </a>
            </div>
          </div>

          <div className="landing-footer-bottom">
            <p className="landing-footer-copy">
              &copy; {new Date().getFullYear()} Spike Hunter Online. All rights reserved.
            </p>
            <p className="landing-footer-disclaimer">
              Trading involves risk. Past performance does not guarantee future results.
            </p>
            <a
              href={`${process.env.REACT_APP_API_BASE_URL || ""}/admin`}
              target="_blank"
              rel="noopener noreferrer"
              className="landing-admin-link"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="landing-admin-link-icon">
                <rect x="5" y="11" width="14" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              Admin
            </a>
          </div>
        </footer>
      </main>
    </div>
  );
}

function FooterIcon({ type }) {
  const s = { fill: "none", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round", strokeLinejoin: "round" };
  const icons = {
    email: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" {...s} />
        <path d="m3 7 9 6 9-6" {...s} />
      </>
    ),
    globe: (
      <>
        <circle cx="12" cy="12" r="9" {...s} />
        <path d="M12 3a15 15 0 0 1 0 18M3 12h18" {...s} />
        <path d="M3.6 7.5h16.8M3.6 16.5h16.8" {...s} />
      </>
    ),
    headset: (
      <>
        <path d="M3 14.5v-2a9 9 0 0 1 18 0v2" {...s} />
        <path d="M3 14.5a2.5 2.5 0 0 0 0 5h1a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1H3Z" {...s} />
        <path d="M21 14.5a2.5 2.5 0 0 1 0 5h-1a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1Z" {...s} />
      </>
    ),
  };
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="landing-footer-icon">
      {icons[type]}
    </svg>
  );
}
